import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Storefront, MagnifyingGlass, MapPin, ArrowRight, Sparkle, CheckCircle, Lightning } from "@phosphor-icons/react";
import api from "../lib/api";
import { getCategory, getCategoryLabel } from "../lib/foodCategories";
import PageMeta from "../components/PageMeta";

/**
 * PublicProfilesDirectory — `/profiles`
 *
 * SEO-friendly index of every published truck profile. No auth required.
 *
 * Sort is server-driven (Pro DESC → complete DESC → name ASC) so the
 * page reflects the same priorities Google and customers should see.
 *
 * URL params:
 *   ?active=1  → show only trucks with an event in the next 14 days.
 *                Driven by the "See all active trucks" CTA on the map.
 *   ?market=   → scope to an SEO market's regional area (e.g.
 *                outer-banks-nc) — server filters by service area.
 *
 * Data source: GET /api/profiles
 */
const DIRECTORY_MARKETS = {
  "outer-banks-nc": {
    label: "Outer Banks",
    phrase: "the Outer Banks",
    href: "/food-trucks/outer-banks-nc",
    mapHref: "/map?lat=35.79&lng=-75.62&zoom=9&label=Outer%20Banks%2C%20NC",
  },
  "lancaster-pa": {
    label: "Lancaster, PA",
    phrase: "Lancaster County",
    href: "/food-trucks/lancaster-pa",
    mapHref: "/map?lat=40.0379&lng=-76.3055&zoom=11&label=Lancaster%2C%20PA",
  },
  "bel-air-md": {
    label: "Bel Air, MD",
    phrase: "Harford County",
    href: "/food-trucks/bel-air-md",
    mapHref: "/map?lat=39.5359&lng=-76.3483&zoom=11&label=Bel%20Air%2C%20MD",
  },
  "newport-news-va": {
    label: "Newport News, VA",
    phrase: "the Virginia Peninsula",
    href: "/food-trucks/newport-news-va",
    mapHref: "/map?lat=37.0871&lng=-76.4730&zoom=11&label=Newport%20News%2C%20VA",
  },
  "orlando-fl": {
    label: "Orlando, FL",
    phrase: "Central Florida",
    href: "/food-trucks/orlando-fl",
    mapHref: "/map?lat=28.5384&lng=-81.3789&zoom=10&label=Orlando%2C%20FL",
  },
};

export default function PublicProfilesDirectory() {
  const [params, setParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeCuisine, setActiveCuisine] = useState(null);
  const [activeOnly, setActiveOnly] = useState(params.get("active") === "1");
  const marketSlug = params.get("market");
  const marketMeta = marketSlug ? DIRECTORY_MARKETS[marketSlug] : null;

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const qs = new URLSearchParams();
    if (activeOnly) qs.set("active_only", "true");
    if (marketMeta) qs.set("market", marketSlug);
    const query = qs.toString() ? `?${qs.toString()}` : "";
    api
      .get(`/profiles${query}`)
      .then(({ data }) => {
        if (!mounted) return;
        setProfiles(Array.isArray(data?.profiles) ? data.profiles : []);
        setCuisines(Array.isArray(data?.cuisines) ? data.cuisines : []);
      })
      .catch(() => mounted && setProfiles([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [activeOnly, marketSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep ?active=1 in the URL in sync with the toggle so the back button
  // and shares work as expected (preserving ?market= and any other params).
  useEffect(() => {
    if (activeOnly && params.get("active") !== "1") {
      const next = new URLSearchParams(params);
      next.set("active", "1");
      setParams(next, { replace: true });
    } else if (!activeOnly && params.get("active") === "1") {
      const next = new URLSearchParams(params);
      next.delete("active");
      setParams(next, { replace: true });
    }
  }, [activeOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return profiles.filter((p) => {
      if (term && !(p.truck_name || "").toLowerCase().includes(term)) return false;
      if (activeCuisine && !(p.cuisine_canonical || []).includes(activeCuisine)) return false;
      return true;
    });
  }, [profiles, q, activeCuisine]);

  return (
    <div className="min-h-dvh bg-background pb-20" data-testid="profiles-directory">
      <PageMeta
        title={marketMeta
          ? `${marketMeta.label} Food Truck Directory | TruckTrackr`
          : "Food Truck Directory — Browse Every Truck | TruckTrackr"}
        description={marketMeta
          ? `Browse food trucks serving ${marketMeta.phrase} — menus, photos, schedules, and live open-now status.`
          : "Browse every food truck on TruckTrackr — menus, photos, schedules, and live open-now status. Find your next favorite truck."}
        canonical="https://thetrucktrackr.com/profiles"
      />
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="h-8 w-8 grid place-items-center rounded-lg bg-primary text-primary-foreground">
              <Storefront weight="duotone" size={18} />
            </span>
            <span className="font-display font-bold text-base tracking-tight">TruckTrackr</span>
          </Link>
          <Link
            to={marketMeta?.mapHref || "/map"}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="profiles-directory-view-map"
          >
            {marketMeta ? `${marketMeta.label} map` : "View map"} <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 pt-8">
        <h1
          className="font-display font-bold text-4xl sm:text-5xl tracking-tight"
          data-testid="profiles-directory-h1"
        >
          {marketMeta ? `${marketMeta.label} food trucks` : "Food trucks worth following"}
        </h1>
        {marketMeta ? (
          <p className="mt-2 text-base text-muted-foreground max-w-2xl leading-relaxed">
            Trucks serving {marketMeta.phrase}.{" "}
            <Link to={marketMeta.href} className="text-primary hover:underline" data-testid="directory-market-back-link">
              See today&apos;s schedules on the {marketMeta.label} page
            </Link>{" "}
            or{" "}
            <Link to="/profiles" className="text-primary hover:underline" data-testid="directory-clear-market-link">
              view all trucks
            </Link>.
          </p>
        ) : (
          <p className="mt-2 text-base text-muted-foreground max-w-2xl leading-relaxed">
            Browse every truck with a public profile on TruckTrackr.
            Pro trucks and complete profiles appear first. In Lancaster County?{" "}
            <Link to="/food-trucks/lancaster-pa" className="text-primary hover:underline" data-testid="directory-lancaster-link">
              See food trucks in Lancaster, PA
            </Link>.
          </p>
        )}

        {/* Filter row — search, active-only toggle */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-xl">
            <MagnifyingGlass
              size={16}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by truck name…"
              className="w-full h-11 pl-9 pr-3 rounded-full border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="profiles-directory-search"
            />
          </div>
          <button
            type="button"
            onClick={() => setActiveOnly((v) => !v)}
            className={`h-11 px-4 rounded-full border text-sm font-semibold transition inline-flex items-center gap-1.5 ${
              activeOnly
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-primary/40"
            }`}
            data-testid="profiles-directory-active-toggle"
          >
            <Lightning weight={activeOnly ? "fill" : "duotone"} size={14} />
            Active this week
          </button>
        </div>

        {/* Cuisine chips */}
        {cuisines.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5" data-testid="profiles-directory-cuisines">
            <button
              type="button"
              onClick={() => setActiveCuisine(null)}
              className={`h-7 px-3 rounded-full text-xs font-semibold border transition ${
                !activeCuisine
                  ? "bg-foreground text-background border-foreground"
                  : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-primary/40"
              }`}
              data-testid="cuisine-chip-all"
            >
              All
            </button>
            {cuisines.map((slug) => {
              const cat = getCategory(slug);
              const active = activeCuisine === slug;
              return (
                <button
                  key={slug}
                  type="button"
                  onClick={() => setActiveCuisine(active ? null : slug)}
                  className={`h-7 px-3 rounded-full text-xs font-semibold border transition inline-flex items-center gap-1 ${
                    active
                      ? "bg-foreground text-background border-foreground"
                      : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-primary/40"
                  }`}
                  data-testid={`cuisine-chip-${slug}`}
                >
                  {cat?.emoji && <span aria-hidden>{cat.emoji}</span>}
                  {getCategoryLabel(slug)}
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <SkeletonGrid />
        ) : filtered.length === 0 ? (
          marketMeta && !(q || activeCuisine || activeOnly) ? (
            <div
              className="mt-10 text-center border border-dashed border-border rounded-2xl p-10"
              data-testid="profiles-directory-market-empty"
            >
              <p className="font-semibold mb-1">No {marketMeta.label} trucks have published a profile yet.</p>
              <p className="text-sm text-muted-foreground">
                Own a food truck in {marketMeta.phrase}?{" "}
                <Link to={marketMeta.href} className="text-primary hover:underline font-semibold">
                  Join on the {marketMeta.label} page
                </Link>{" "}
                — early trucks get founding perks.
              </p>
            </div>
          ) : (
            <EmptyState
              hasFilter={!!(q || activeCuisine || activeOnly)}
              onClear={() => { setQ(""); setActiveCuisine(null); setActiveOnly(false); }}
            />
          )
        ) : (
          <ul
            className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="profiles-directory-grid"
          >
            {filtered.map((p) => (
              <ProfileCard key={p.truck_slug} profile={p} />
            ))}
          </ul>
        )}

        {!loading && filtered.length > 0 && (
          <div className="mt-6 text-xs text-muted-foreground text-center">
            Showing {filtered.length} of {profiles.length} {profiles.length === 1 ? "truck" : "trucks"}
            {activeOnly && " active this week"}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileCard({ profile }) {
  const tags = (profile.cuisine_canonical?.length ? profile.cuisine_canonical : profile.cuisine_tags || []).slice(0, 2);
  return (
    <li>
      <Link
        to={`/t/${profile.truck_slug}`}
        className="block rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 hover:shadow-lg transition"
        data-testid={`profile-card-${profile.truck_slug}`}
      >
        <div className="aspect-[16/9] bg-muted overflow-hidden relative">
          {profile.cover_url ? (
            <img
              src={profile.cover_url}
              alt={`${profile.truck_name} cover photo`}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full grid place-items-center text-muted-foreground/50">
              <Storefront size={32} weight="duotone" />
            </div>
          )}
          {profile.is_pro && (
            <span
              className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-primary to-amber-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-sm"
              data-testid="profile-card-pro-badge"
            >
              <Sparkle weight="fill" size={10} /> Pro
            </span>
          )}
          {profile.logo_url && (
            <div className="absolute -bottom-3 left-3 h-12 w-12 rounded-xl border-2 border-card bg-card overflow-hidden">
              <img
                src={profile.logo_url}
                alt={`${profile.truck_name} logo`}
                className="h-full w-full object-cover"
              />
            </div>
          )}
        </div>
        <div className={`p-4 ${profile.logo_url ? "pt-5" : ""}`}>
          <div className="flex items-start justify-between gap-2">
            <h2 className="font-display font-bold text-base leading-snug line-clamp-1">
              {profile.truck_name}
            </h2>
            {profile.is_complete && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400 flex-none"
                title="Complete profile"
                data-testid="profile-card-complete-badge"
              >
                <CheckCircle weight="fill" size={11} /> Complete
              </span>
            )}
          </div>
          {profile.short_description && (
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed line-clamp-2">
              {profile.short_description}
            </p>
          )}
          {(tags.length > 0 || profile.service_area) && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
              {tags.map((slug) => {
                const cat = getCategory(slug);
                return (
                  <span
                    key={slug}
                    className="inline-flex items-center gap-1 px-2 h-5 rounded-full bg-muted"
                  >
                    {cat?.emoji && <span aria-hidden>{cat.emoji}</span>}
                    {getCategoryLabel(slug)}
                  </span>
                );
              })}
              {profile.service_area && (
                <span className="inline-flex items-center gap-1">
                  <MapPin size={11} weight="duotone" />
                  <span className="truncate max-w-[140px]">{profile.service_area}</span>
                </span>
              )}
            </div>
          )}
        </div>
      </Link>
    </li>
  );
}

function SkeletonGrid() {
  return (
    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" aria-busy="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="aspect-[16/9] bg-muted animate-pulse" />
          <div className="p-4 space-y-2">
            <div className="h-4 w-2/3 bg-muted animate-pulse rounded" />
            <div className="h-3 w-full bg-muted animate-pulse rounded" />
            <div className="h-3 w-1/2 bg-muted animate-pulse rounded" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyState({ hasFilter, onClear }) {
  if (hasFilter) {
    return (
      <div className="mt-12 text-center" data-testid="profiles-directory-empty-filtered">
        <Storefront size={36} weight="duotone" className="text-muted-foreground/50 mx-auto" />
        <p className="mt-2 text-sm text-muted-foreground">No matches.</p>
        <button
          type="button"
          onClick={onClear}
          className="mt-3 text-xs font-semibold text-primary hover:underline"
        >
          Clear filters
        </button>
      </div>
    );
  }
  return (
    <div className="mt-12 text-center" data-testid="profiles-directory-empty">
      <Storefront size={36} weight="duotone" className="text-muted-foreground/50 mx-auto" />
      <h2 className="mt-3 font-display font-bold text-lg">No published profiles yet</h2>
      <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">
        Owners can build a public page from their dashboard. Check back soon — new trucks are joining every week.
      </p>
      <Link
        to="/map"
        className="mt-4 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 transition"
      >
        Browse the map
      </Link>
    </div>
  );
}
